import { useMemo } from "react";
import Images from "../../utils/Images";

// Map policy types to filter keys
const policyTypeMap = {
  daily_commuter: "daily",
  intercity_travel: "intercity",
  okada_rider: "bikers",
  taxi_and_bolt: "drivers",
};

export default function useInsuranceData(
  selectedKey,
  secondaryTab,
  policies = [],
  advancedFilters = {}
) {
  const allItems = useMemo(() => {
    if (!policies || policies.length === 0) {
      return [];
    }

    // Filter policies based on selectedKey
    let filteredPolicies = policies.filter((policy) => {
      // Handle TripSecure insurance - check by policy type mapping and short_description
      if (policy.short_description === "tripsecure_insurance") {
        const mappedType = policyTypeMap[policy.policy_type];
        if (mappedType === selectedKey) {
          return true;
        }
        // Also handle "tripsecure" key for all TripSecure policies
        if (selectedKey === "tripsecure") {
          return true;
        }
      }
      
      // Handle Travel insurance - check by policy type mapping and short_description
      if (policy.short_description === "travel_insurance") {
        const mappedType = policyTypeMap[policy.policy_type];
        if (mappedType === selectedKey) {
          return true;
        }
        // Also handle "travel" key for all Travel policies
        if (selectedKey === "travel") {
          return true;
        }
      }
      
      // Handle old travel insurance types (backward compatibility - no short_description)
      const mappedType = policyTypeMap[policy.policy_type];
      if (mappedType === selectedKey && !policy.short_description) {
        return true;
      }
      
      // Handle life insurance
      if (selectedKey === "life" && policy.short_description === "life_insurance") {
        return true;
      }
      
      // Handle general insurance
      if (selectedKey === "general" && policy.short_description === "general_insurance") {
        return true;
      }
      
      return false;
    });

    // Apply advanced filters if provided
    if (advancedFilters && Object.keys(advancedFilters).length > 0) {
      filteredPolicies = filteredPolicies.filter((policy) => {
        // Premium range filter
        if (advancedFilters.minPremium || advancedFilters.maxPremium) {
          const prices = policy.coverage_durations.map(
            (duration) => duration.price || duration.premium || 0
          );
          const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
          const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

          if (
            advancedFilters.minPremium &&
            minPrice < advancedFilters.minPremium
          )
            return false;
          if (
            advancedFilters.maxPremium &&
            maxPrice > advancedFilters.maxPremium
          )
            return false;
        }

        // Duration filter
        if (advancedFilters.duration) {
          const durations = policy.coverage_durations.map((duration) => {
            // Handle new format with display_unit and display_value
            if (duration.display_unit && duration.display_value) {
              return duration.display_value;
            }
            // Handle old format with days
            if (duration.days) {
              return duration.days;
            }
            // Handle hours format
            if (duration.hours) {
              return duration.hours;
            }
            // Handle string duration format (e.g., "3 hours", "5 days")
            if (typeof duration.duration === "string") {
              const match = duration.duration.match(/^(\d+)\s*(hours?|days?|months?|years?)$/i);
              if (match) {
                return parseInt(match[1], 10);
              }
            }
            return 0;
          });
          if (!durations.includes(advancedFilters.duration)) return false;
        }

        // Age range filter (based on eligibility criteria)
        if (advancedFilters.ageRange) {
          const eligibilityText = policy.eligibility_criteria
            .join(" ")
            .toLowerCase();
          const ageRange = advancedFilters.ageRange;

          if (
            ageRange === "18-25" &&
            !eligibilityText.includes("18") &&
            !eligibilityText.includes("25")
          )
            return false;
          if (
            ageRange === "26-40" &&
            !eligibilityText.includes("26") &&
            !eligibilityText.includes("40")
          )
            return false;
          if (
            ageRange === "41-60" &&
            !eligibilityText.includes("41") &&
            !eligibilityText.includes("60")
          )
            return false;
        }

        // Coverage type filter (based on insurance coverage list)
        if (advancedFilters.coverageType) {
          const coverageTypes = policy.insurance_coverage_list.map((coverage) =>
            coverage.title.toLowerCase()
          );
          const searchType = advancedFilters.coverageType.toLowerCase();
          if (!coverageTypes.some((type) => type.includes(searchType)))
            return false;
        }

        // Claims process time filter
        if (advancedFilters.claimsTime) {
          const claimSteps = policy.claim_process_steps;
          const timeframe = claimSteps[0]?.timeframe || "";

          if (
            advancedFilters.claimsTime === "instant" &&
            !timeframe.includes("24")
          )
            return false;
          if (
            advancedFilters.claimsTime === "1-3days" &&
            !timeframe.includes("32")
          )
            return false;
          if (
            advancedFilters.claimsTime === "up-to-7days" &&
            !timeframe.includes("7")
          )
            return false;
        }

        return true;
      });
    }

    // Transform policies to match the expected card format
    const items = filteredPolicies.map((policy, index) => {
      // Get price range from coverage_durations
      // Handle both travel insurance (price) and life/general insurance (premium)
      const prices = policy.coverage_durations.map(
        (duration) => duration.price || duration.premium || 0
      );
      const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
      const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

      // Get coverage days range
      const days = policy.coverage_durations.map((duration) => {
        // Handle new format with display_unit and display_value
        if (duration.display_unit && duration.display_value) {
          return duration.display_value;
        }
        // Handle old format with days
        if (duration.days) {
          return duration.days;
        }
        // Handle hours format
        if (duration.hours) {
          return duration.hours;
        }
        // Handle string duration format (e.g., "3 hours", "5 days") - for travel insurance
        if (typeof duration.duration === "string") {
          const match = duration.duration.match(/^(\d+)\s*(hours?|days?|months?|years?)$/i);
          if (match) {
            const value = parseInt(match[1], 10);
            const unit = match[2].toLowerCase();
            // Convert to a common unit (hours) for comparison
            if (unit.startsWith("day")) return value * 24;
            if (unit.startsWith("month")) return value * 24 * 30;
            if (unit.startsWith("year")) return value * 24 * 365;
            return value; // hours
          }
        }
        return 0;
      });
      const minDays = days.length > 0 ? Math.min(...days) : 0;
      const maxDays = days.length > 0 ? Math.max(...days) : 0;

      // Extract perks from insurance_coverage_list
      const perks = policy.insurance_coverage_list.map(
        (coverage) => coverage.title
      );

      // Generate a random sold count for display
      const sold = Math.floor(Math.random() * 200) + 50;

      // Determine rank based on secondary tab
      const tagRank =
        secondaryTab === "featured"
          ? index % 5
          : secondaryTab === "new"
          ? 4 - (index % 5)
          : secondaryTab === "recommended"
          ? index % 3
          : index % 2;

      return {
        id: policy.slug,
        provider: typeof policy.insurance_provider === 'object' 
          ? policy.insurance_provider?.company_name || "SUNU Assurance"
          : policy.insurance_provider || "SUNU Assurance",
        premiumMin: minPrice,
        premiumMax: maxPrice,
        coverDays: minDays,
        maxDays: maxDays,
        sold,
        logo: Images.sunu,
        perks: perks.slice(0, 2),
        extraPerks: perks.slice(2),
        rank: tagRank,
        // Include original policy data for detailed view
        originalPolicy: policy,
        policyName: policy.policy_name,
        policy_type: policy.policy_type,
        description: policy.description,
        shortDescription: policy.short_description,
        riskLevel: policy.risk_level,
        geography: policy.geography,
        // Backward compat: policy-level max_claims (TripSecure) or max of package max_claims (Life/General/Travel)
        maxClaims:
          policy.max_claims ??
          (policy.coverage_durations?.length &&
          policy.coverage_durations.some((d) => d.max_claims != null)
            ? Math.max(
                ...policy.coverage_durations.map((d) => Number(d.max_claims) || 0)
              )
            : undefined),
        waitingPeriod: policy.waiting_period,
        eligibilityCriteria: policy.eligibility_criteria,
        exclusions: policy.exclusions,
        terms: policy.terms,
        claimProcessSteps: policy.claim_process_steps,
        supportContact: policy.support_contact,
        policyRules: policy.policy_rules,
        companyLogo: policy.insurance_provider?.company_logo || Images.logo_colored,
      };
    });

    return items.sort((a, b) => a.rank - b.rank);
  }, [selectedKey, secondaryTab, policies, advancedFilters]);

  return allItems;
}
